<template>
  <div class="main" :class="{center: creating_pool || joining_pool}">
    <LoadingSpinner v-if="loading" message="Gruppen werden geladen..."/>
    <BackButton v-if="pool"/>
    <h1 v-if="pool">Gruppeninfos</h1>
    <div class="pool-infos frosted" v-if="pool">
      <h2>{{pool.name}}</h2>
      <p>Erstellt am <b>{{pool.created_at}}</b></p>
      <p><b>{{pool.members}} / {{pool.member_limit}}</b> Mitglieder <span v-if="pool.members >= pool.member_limit" class="limit-warning">(Limit erreicht)</span></p>
      <p v-if="poolAdmins && poolAdmins.length > 0">Admin: <b>{{poolAdmins.join(', ')}}</b></p>
      <div class="tag-row">
        <div v-if="pool.isUserAdmin" class="admin-tag">Du bist Admin</div>
        <div v-if="pool.member_limit > 5" class="premium-tag">Premium Gruppe ({{pool.member_limit}} Personen)</div>
      </div>
      <p class="invite-link" @click="copyCode" :title="'Code kopieren: ' + pool.code">
        Einladungscode: <b>{{pool.code}}</b>
        <span v-if="showCopiedMessage" class="copy-badge">Kopiert!</span>
      </p>

      <div class="qr-container frosted">
        <QRCodeVue :value="'https://app.clinkapp.de/join/' + pool.code" :size="200" level="H" class="qr-code" />
        <p class="qr-hint">Lass andere diesen Code scannen, um beizutreten.</p>
      </div>
      <button class="danger single" @click="leaving_pool = true" v-if="!leaving_pool">Gruppe verlassen</button>
      <div v-if="leaving_pool" class="leaving-pool">
        <p>Sicher, dass du die Gruppe verlassen willst?</p>
        <div class="buttons">
          <button @click="leaving_pool = false">Abbrechen</button>
          <button class="danger" @click="leavePool()">Verlassen</button>
        </div>
      </div>
    </div>

    <!-- Upgrade Section -->
    <div class="upgrade-section frosted" v-if="pool && pool.isUserAdmin && pool.member_limit < 100">
      <h3>Gruppen-Upgrade</h3>
      <p>Erhöhe das Limit deiner Gruppe für mehr Mitglieder.</p>
      <div class="upgrade-cards">
        <div class="upgrade-card" v-if="pool.member_limit < 15" @click="upgradePool('tier_15')">
          <div class="upgrade-header">
            <h4>Bis zu 15 Personen</h4>
            <span class="price">1,00€</span>
          </div>
          <p>Ideal für kleine Teams und Vereine. <b>Inklusive Statistik-Dashboard.</b></p>
          <button class="upgrade-button">Jetzt upgraden</button>
        </div>
        <div class="upgrade-card" v-if="pool.member_limit < 100" @click="upgradePool('tier_100')">
          <div class="upgrade-header">
            <h4>Bis zu 100 Personen</h4>
            <span class="price">5,00€</span>
          </div>
          <p>Für große Events und große Gruppen. <b>Inklusive Statistik-Dashboard.</b></p>
          <button class="upgrade-button">Jetzt upgraden</button>
        </div>
      </div>
      <p class="legal-disclaimer">
        Mit dem Kauf akzeptierst du unsere <router-link to="/terms-of-service">AGB</router-link>. 
        Du bestätigst die sofortige Ausführung des Upgrades und verlierst damit dein <router-link to="/withdrawal">Widerrufsrecht</router-link>.
      </p>
    </div>

    <div class="enter-group frosted" v-if="creating_pool">
      <button class="back_button" @click="setup()"><img src="@/assets/images/back.svg">Zurück</button>
      <h2>Erstelle jetzt deine Gruppe</h2>
      <p class="limit-hint">Kostenlose Gruppen sind auf 5 Mitglieder begrenzt.</p>
      <form v-on:submit.prevent="createPool">
        <div class="form-item">
          <label for="new_group_name">Gruppenname</label>
          <input type="text" id="new_group_name" v-model="newGroupName">
        </div>
        <div class="form-item center">
          <button type="submit">Erstellen</button>
        </div>
      </form>
    </div>
    <div class="enter-group frosted" v-if="joining_pool">
      <button class="back_button" @click="setup()"><img src="@/assets/images/back.svg">Zurück</button>
      <h2>Tritt einer Gruppe bei</h2>
      <form v-on:submit.prevent="joinPool">
        <div class="form-item">
          <label for="new_group_name">Einladungs-Code</label>
          <div class="input-with-scanner">
            <input type="text" id="new_group_name" v-model="inviteCode" placeholder="Z.B. ABCDE">
          </div>
          <QRScanner @scan="onQRScanned" />
        </div>
        <div class="form-item terms-of-use-container">
          <input type="checkbox" id="terms-of-use" v-model="termsOfUseRead">
          <label for="terms-of-use">Ich bestätige, während der Benutzung der App lediglich korrekte Angaben zu meinem Alkoholkonsum zu machen. Mir ist bewusst, dass Falschangaben, und vor allem das bewusste Eintragen von höheren Mengen an konsumiertem Alkohol als tatsächlich geschehen ein schweres Vergehen darstellen und mit Strafen sowie dem Erlischen des Zugangs zur App geahndet werden können. </label>
        </div>
        <div class="form-item center">
          <button type="submit" :disabled="!termsOfUseRead || !inviteCode">Beitreten</button>
        </div>
      </form>
    </div>
    <h1 v-if="available_pools && !creating_pool && !joining_pool">Deine Gruppen</h1>
    <div class="available-pools" v-if="available_pools && !creating_pool && !joining_pool && !pool">
      <div class="pool-infos list frosted" v-for="pool in available_pools" :key="pool.pool_id">
        <div class="pool-infos no-padding">
          <h2>{{pool.name}}</h2>
          <p>Erstellt am <b>{{pool.created_at}}</b></p>
          <p><b>{{pool.members}}</b> Mitglieder</p>
        </div>
        <router-link :to="'/pool-settings?pool=' + pool.pool_id">Details</router-link>
      </div>

      <div class="no-pools-notification" v-if="available_pools.length === 0">
        <h2>Noch keine Gruppen vorhanden</h2>
      </div>

      <div class="action-buttons">
        <button class="add_pool frosted" @click="joining_pool = true">
          <img src="@/assets/images/group.svg">
          <h3>Gruppe beitreten</h3>
        </button>
        <button class="add_pool frosted" @click="creating_pool = true">
          <img src="@/assets/images/group_add.svg">
          <h3>Neue Gruppe</h3>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import AuthService from '@/services/AuthService.js';
import moment from 'moment';
import BackButton from "@/components/BackButton.vue";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import QRCodeVue from 'qrcode.vue';
import QRScanner from "@/components/QRScanner.vue";
export default {
  "name": "PoolSettingsView",
  components: {LoadingSpinner, BackButton, QRCodeVue, QRScanner},
  data() {
    return {
      isAdmin: false,
      loading: true,
      pool: null,
      creating_pool: false,
      inviteCode: null,
      termsOfUseRead: false,
      joining_pool: false,
      leaving_pool: false,
      newGroupName: null,
      available_pools: null,
      poolAdmins: [],
      showCopiedMessage: false,
    }
  },
  watch:{
    $route (){
      this.setup();
    }
  },
  methods: {
    logout() {
      this.$store.dispatch('logout');
      this.$router.push('/login');
    },
    async setup() {
      this.available_pools = null;
      this.pool = null;
      this.creating_pool = false;
      this.joining_pool = false;

      // Check for join_code in query (from invitation link redirect)
      if (this.$route.query.join_code) {
        this.joining_pool = true;
        this.inviteCode = this.$route.query.join_code;
      }

      try {
        this.msg = await AuthService.getUser()
        this.isAdmin = !!this.msg.msg.isAdmin;
      } catch(e){
        console.error(e)
      }


      if(this.$route.query.pool){
        await this.getPoolData(this.$route.query.pool)
      } else {
        this.msg = await AuthService.getUserJoinedPools()
        if(this.msg.success) {
          this.available_pools = this.msg.msg.map(pool => {return {...pool, created_at: moment(pool.created_at).format("DD.MM.YYYY")}})
        }
        this.loading = false;
      }
    },
    async createPool() {
      this.msg = await AuthService.createPool({"new_group_name": this.newGroupName});
      if (this.msg.success) {
        const poolMsg = await AuthService.getPool({pool_id: this.msg.newPoolId});
        if (poolMsg.success) {
          this.$store.commit('SET_SELECTED_POOL', poolMsg.msg);
        }
        this.$router.push('/pool-settings?pool=' + this.msg.newPoolId)
      } else {
        this.msg = this.msg.msg
      }
    },
    async joinPool() {
      this.msg = await AuthService.joinPool({"invite_code": this.inviteCode.toUpperCase()});
      if (this.msg.success) {
        localStorage.removeItem('pending_invite_code');
        const poolMsg = await AuthService.getPool({pool_id: this.msg.newPoolId});
        if (poolMsg.success) {
          this.$store.commit('SET_SELECTED_POOL', poolMsg.msg);
        }
        this.$router.push('/pool-settings?pool=' + this.msg.newPoolId)
      } else {
        this.msg = this.msg.msg
      }
    },
    onQRScanned(code) {
      this.inviteCode = code;
      // Auto-accept terms and join immediately when scanned via QR
      this.termsOfUseRead = true;
      this.joinPool();
    },
    async leavePool() {
      this.msg = await AuthService.leavePool({"pool_id": this.$route.query.pool});
      if (this.msg.success) {
        this.$router.push('/pool-settings')
      } else {
        this.msg = this.msg.msg
      }
    },
    async getPoolData(id){
      let msg = await AuthService.getPool({pool_id: id});
      if(msg.success){
        this.pool = {...msg.msg, created_at: moment(msg.msg.created_at).format("DD.MM.YYYY")}
        this.$store.commit('SET_SELECTED_POOL', msg.msg);

        // Fetch admins
        try {
          const membersMsg = await AuthService.getUsersInPool({ pool_id: id });
          if (membersMsg.success) {
            this.poolAdmins = membersMsg.msg
                .filter(m => m.is_admin)
                .map(m => m.name || m.email);
          }
        } catch (e) {
          console.error("Error fetching pool admins:", e);
        }
      }
      this.loading = false;
     },
    async upgradePool(tier) {
      try {
        const stripe = window.Stripe(process.env.VUE_APP_STRIPE_PUBLIC_KEY || 'pk_test_placeholder');
        const response = await AuthService.createStripeSession({
          poolId: this.pool.id,
          tier: tier
        });
        
        if (response.id) {
          const { error } = await stripe.redirectToCheckout({
            sessionId: response.id
          });
          if (error) {
            console.error(error);
            alert('Stripe Error: ' + error.message);
          }
        }
      } catch (e) {
        console.error(e);
        alert('Fehler beim Starten des Bezahlvorgangs.');
      }
    },
    async copyCode() {
      if (!this.pool?.code) return;
      
      const inviteLink = `${window.location.origin}/join/${this.pool.code}`;
      
      try {
        await navigator.clipboard.writeText(inviteLink);
        this.showCopiedMessage = true;
        
        // Reset message after animation finishes
        setTimeout(() => {
          this.showCopiedMessage = false;
        }, 1500);
      } catch (err) {
        console.error('Failed to copy: ', err);
      }
    }
    },

    async beforeCreate() {
      if (!this.$store.getters.isLoggedIn) {
        this.$router.push('/login');
      }
    },
    async mounted() {
      await this.setup()
    }
}
</script>
<style scoped>
.main {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  height: 100%;
  background: var(--background);
  padding: 20px;
  overflow-y: auto;
}

.main.center {
  justify-content: center;
}

h1 {
  font-size: 1.8rem;
  margin: 10px 0 20px 0;
}

h2 {
  text-align: left;
  margin: 0;
  font-weight: 600;
  font-size: 1.2rem;
}

p {
  color: var(--tertiary-color);
  margin: 0;
}

/* Pool Card */
.pool-infos {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background: var(--background);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-l);
  padding: 20px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.05);
  box-sizing: border-box;
}

.pool-infos img {
  width: 100%;
  max-height: 200px;
  object-fit: cover;
  border-radius: var(--radius-m);
  margin-bottom: 15px;
}

.pool-infos h2 {
  font-size: 1.5rem;
  margin-bottom: 5px;
}

.invite-link {
  background: var(--background-secondary);
  padding: 12px 20px;
  border-radius: var(--radius-m);
  align-self: center;
  margin-top: 20px !important;
  font-size: 1.1rem;
  transition: all 0.2s ease-in-out;
  cursor: pointer;
  color: var(--primary-color);
  border: 1px solid var(--border-color);
  position: relative;
}

.copy-badge {
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--primary-color);
  color: var(--background);
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 700;
  pointer-events: none;
  box-shadow: 0 4px 10px rgba(0,0,0,0.2);
  white-space: nowrap;
  animation: floatUpFade 1.5s ease-out forwards;
  z-index: 10;
}

@keyframes floatUpFade {
  0% { transform: translate(-50%, 0); opacity: 0; }
  10% { transform: translate(-50%, -10px); opacity: 1; }
  100% { transform: translate(-50%, -40px); opacity: 0; }
}

.invite-link:active {
  background: var(--active-bg);
}

/* QR Code */
.qr-container {
  align-self: center;
  margin: 30px 0 20px 0;
  padding: 25px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  background: white !important; /* Always light for scanability */
  border-radius: var(--radius-l);
}

.qr-code {
  border: 10px solid white;
  border-radius: var(--radius-m);
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
}

.qr-hint {
  font-size: 0.85rem;
  font-weight: 500;
  color: #000000; /* Dark text on white bg */
  opacity: 0.8;
}

.input-with-scanner {
  width: 100%;
}

/* List Items */
.available-pools {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  width: 100%;
  max-width: 600px;
  padding-bottom: 100px;
}

.pool-infos.list {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border: none;
  box-shadow: none;
  cursor: pointer;
  transition: background 0.2s;
  box-sizing: border-box;
}

.pool-infos.list:active {
  background: var(--active-bg);
}

.pool-infos.list .no-padding {
  padding: 0;
  background: none;
  border: none;
  box-shadow: none;
  width: auto;
}

.pool-infos.list a {
  background: var(--primary-color);
  color: var(--text-on-primary);
  text-decoration: none;
  padding: 8px 16px;
  border-radius: var(--radius-s);
  font-size: 0.9rem;
  font-weight: 500;
}

/* Forms */
.enter-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 100%;
  max-width: 400px;
  padding: 20px;
  border-radius: var(--radius-l);
  box-sizing: border-box;
}

.enter-group input[type="text"] {
  font-size: 1.2rem;
  border: none;
  border-radius: var(--radius-m);
  padding: 15px;
  background-color: var(--background-secondary);
  color: var(--primary-color);
  width: 100%;
  box-sizing: border-box;
}

.enter-group input::placeholder {
  color: var(--tertiary-color);
}

.enter-group form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
}

.form-item label {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--primary-color);
  margin-left: 5px;
}

.terms-of-use-container {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 10px;
}

.terms-of-use-container input {
  margin-top: 5px;
}

.terms-of-use-container label {
  font-size: 0.8rem;
  line-height: 1.4;
  color: var(--tertiary-color);
  text-align: left;
}

/* Buttons */
button {
  font-family: var(--font);
  color: var(--background);
  border: none;
  font-size: 1rem;
  font-weight: 600;
  background: var(--primary-color);
  border-radius: var(--radius-l);
  padding: 16px 32px;
  transition: all ease 0.2s;
  cursor: pointer;
  align-self: center;
}

button:active {
  opacity: 0.8;
}

button:disabled {
  background-color: var(--background-secondary);
  color: var(--tertiary-color);
  cursor: not-allowed;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 100%;
}

button.add_pool {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  width: 100%;
  justify-content: center;
  background: var(--background);
}

button.add_pool h3 {
  margin: 0;
  font-size: 1rem;
  color: var(--primary-color);
}

button.add_pool img {
  width: 24px;
  height: 24px;
  filter: var(--icon-filter);
}

button.add_pool.secondary {
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
}

button.add_pool.secondary h3 {
  color: var(--primary-color);
}

button.add_pool.secondary img {
  filter: var(--icon-filter);
}

.back_button {
  background: none;
  padding: 0;
  color: var(--primary-color);
  align-self: flex-start;
  display: flex;
  align-items: center;
  font-size: 1rem;
  margin-bottom: 20px;
}

.limit-hint {
  font-size: 0.8rem;
  font-style: italic;
  margin-bottom: 10px;
  text-align: left;
}

.limit-warning {
  color: var(--warning);
  font-weight: 600;
  font-size: 0.8rem;
}

.premium-tag {
  background: var(--success);
  color: white;
  padding: 4px 10px;
  border-radius: var(--radius-s);
  font-size: 0.75rem;
  font-weight: 700;
  margin-top: 5px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.admin-tag {
  background: var(--primary-color);
  color: var(--text-on-primary);
  padding: 4px 10px;
  border-radius: var(--radius-s);
  font-size: 0.75rem;
  font-weight: 700;
  margin-top: 5px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.tag-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 5px;
}

.back_button img {
  width: 20px;
  height: 20px;
  margin-right: 5px;
  filter: var(--icon-filter);
}

button.danger {
  background: var(--warning);
  color: white;
}

button.danger.single {
  margin-top: 10px;
}

.leaving-pool {
  margin-top: 20px;
  align-self: center;
  background: var(--background-secondary);
  padding: 20px;
  border-radius: var(--radius-m);
  text-align: center;
}

.leaving-pool .buttons {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 15px;
}

.leaving-pool button {
  padding: 10px 20px;
  font-size: 0.9rem;
}

.leaving-pool button:first-child {
  background: var(--background);
  color: var(--primary-color);
}


.upgrade-section {
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 100%;
  max-width: 400px;
  margin-top: 20px;
  margin-bottom: 20px;
  padding: 20px;
  box-sizing: border-box;
}

.upgrade-section h3 {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
}

.upgrade-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.upgrade-card {
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-m);
  padding: 15px;
  cursor: pointer;
  transition: transform 0.2s, border-color 0.2s;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.upgrade-card:hover {
  transform: translateY(-2px);
  border-color: var(--primary-color);
}

.upgrade-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.upgrade-header h4 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}

.upgrade-card .price {
  font-weight: 700;
  color: var(--primary-color);
  font-size: 1.1rem;
}

.upgrade-card p {
  font-size: 0.85rem;
  margin: 0;
}

.upgrade-button {
  margin-top: 10px;
  padding: 8px 15px;
  font-size: 0.85rem;
  width: 100%;
}

.legal-disclaimer {
  font-size: 0.75rem;
  color: var(--tertiary-color);
  margin-top: 15px;
  line-height: 1.4;
  text-align: center;
}

.legal-disclaimer a {
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 600;
}

.no-pools-notification {
  margin-top: 25vh;
}
</style>

